import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  getAdminSettings,
  updateAdminSettings,
  sendDailyRemindersNow,
  uploadWhatsappTreeGreetingImage,
  uploadWhatsappTreeFollowupImage,
  resolveMediaUrl,
  superAdminResetAllAppointments,
  SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE,
  superAdminResetAllClinicalRecords,
  SUPER_RESET_CLINICAL_CONFIRM_PHRASE,
  getBookingPhoneStats,
  resetPhoneNoShowCount,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { toast } from 'sonner';
import { Ban, ClipboardList, FileText, Image as ImageIcon, Plus, Save, Settings2, Sparkles, Sun, Trash2, X } from 'lucide-react';

function FlowStep({ step, title, hint, children }) {
  return (
    <section className="rounded-xl border border-border bg-card/50 p-4 space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">{step}</p>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {hint ? <p className="text-sm text-muted-foreground mt-1">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

const initialSettings = {
  wa_booking_whatsapp_enabled: false,
  wa_booking_whatsapp_template: '',
  wa_confirmation_whatsapp_enabled: true,
  wa_confirmation_whatsapp_template: '',
  appointment_reminder_enabled: true,
  appointment_reminder_minutes_before: 120,
  appointment_reminder_check_interval_sec: 60,
  public_base_url: 'http://localhost',
  appointment_reminder_template: '',
  appointment_daily_reminder_template: '',
  appointment_daily_reminder_time: '08:00',
  appointment_daily_attendance_deadline_time: '10:00',
  appointment_daily_auto_cancel_template: '',
  wa_tree_greeting_image_url: '',
  wa_tree_followup_image_url: '',
  wa_tree_greeting_text: '',
  wa_phone_blacklist: [],
  consent_podiatrist_name: '',
  consent_podiatrist_license: '',
  no_show_max_before_block: 2,
  booking_transfer_amount_label: '$300 MXN',
  booking_transfer_reminder_template:
    'Recuerda realizar tu transferencia por {amount} para confirmar tu cita.',
  booking_transfer_at_limit_template: 'Ahora el costo para agendar tu cita es de {amount}.',
  urgent_appointment_amount_label: '$500 MXN',
  wa_ai_enabled: false,
  wa_ai_base_url: 'https://api.openai.com/v1',
  wa_ai_model: 'gpt-4o-mini',
  wa_ai_api_key: '',
  wa_ai_business_context: '',
  wa_allow_same_day_standard: false,
};

const WA_AI_API_KEY_MASK = '********';

export default function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [forceDailyResend, setForceDailyResend] = useState(false);
  const [uploadingWaGreeting, setUploadingWaGreeting] = useState(false);
  const [uploadingWaFollowup, setUploadingWaFollowup] = useState(false);
  const [resetAppointmentsBusy, setResetAppointmentsBusy] = useState(false);
  const [resetNoShowPhone, setResetNoShowPhone] = useState('');
  const [resetNoShowBusy, setResetNoShowBusy] = useState(false);
  const [noShowList, setNoShowList] = useState([]);
  const [noShowListLoading, setNoShowListLoading] = useState(false);
  const [noShowListError, setNoShowListError] = useState('');
  const [noShowAtLimitOnly, setNoShowAtLimitOnly] = useState(false);
  const [unblockingPhone, setUnblockingPhone] = useState(null);
  const [resetAppointmentsPhrase, setResetAppointmentsPhrase] = useState('');
  const [resetClinicalBusy, setResetClinicalBusy] = useState(false);
  const [resetClinicalPhrase, setResetClinicalPhrase] = useState('');
  const [blacklistInput, setBlacklistInput] = useState('');
  const [blacklistNameInput, setBlacklistNameInput] = useState('');

  const normalizeBlacklistPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length < 10) return null;
    return digits.slice(-10);
  };

  const formatBlacklistPhone = (phone) => {
    const p = String(phone || '');
    if (p.length === 10) return `${p.slice(0, 3)} ${p.slice(3, 6)} ${p.slice(6)}`;
    return p;
  };

  const coerceBlacklistEntries = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const phone = normalizeBlacklistPhone(item.phone ?? item);
          if (!phone) return null;
          return {
            phone,
            name: String(item.name || item.label || '').trim().slice(0, 80),
          };
        }
        const phone = normalizeBlacklistPhone(item);
        if (!phone) return null;
        return { phone, name: '' };
      })
      .filter(Boolean);
  };

  const persistBlacklist = async (nextList) => {
    const payload = {
      ...settings,
      appointment_reminder_minutes_before: Number(settings.appointment_reminder_minutes_before),
      appointment_reminder_check_interval_sec: Number(settings.appointment_reminder_check_interval_sec),
      appointment_daily_reminder_time: (settings.appointment_daily_reminder_time || '').trim(),
      wa_phone_blacklist: nextList,
    };
    const saved = await updateAdminSettings(payload);
    setSettings({
      ...initialSettings,
      ...saved,
      wa_phone_blacklist: coerceBlacklistEntries(saved.wa_phone_blacklist),
    });
    return saved;
  };

  const handleAddBlacklistPhone = async () => {
    const norm = normalizeBlacklistPhone(blacklistInput);
    if (!norm) {
      toast.error('Escribe un número válido (10 dígitos, con o sin +52)');
      return;
    }
    const current = coerceBlacklistEntries(settings.wa_phone_blacklist);
    if (current.some((e) => e.phone === norm)) {
      toast.message('Ese número ya está en la lista negra');
      return;
    }
    const name = String(blacklistNameInput || '').trim().slice(0, 80);
    try {
      await persistBlacklist([...current, { phone: norm, name }]);
      setBlacklistInput('');
      setBlacklistNameInput('');
      toast.success('Número bloqueado y guardado');
    } catch {
      toast.error('No se pudo guardar la lista negra');
    }
  };

  const handleRemoveBlacklistPhone = async (phone) => {
    const current = coerceBlacklistEntries(settings.wa_phone_blacklist);
    try {
      await persistBlacklist(current.filter((e) => e.phone !== phone));
      toast.success('Número quitado de la lista negra');
    } catch {
      toast.error('No se pudo actualizar la lista negra');
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getAdminSettings();
        setSettings({
          ...initialSettings,
          ...data,
          wa_phone_blacklist: coerceBlacklistEntries(data.wa_phone_blacklist),
        });
      } catch {
        toast.error('No se pudo cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        appointment_reminder_minutes_before: Number(settings.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(settings.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (settings.appointment_daily_reminder_time || '').trim(),
        appointment_daily_attendance_deadline_time: (
          settings.appointment_daily_attendance_deadline_time || '10:00'
        ).trim(),
        no_show_max_before_block: Math.max(1, Number(settings.no_show_max_before_block) || 2),
      };
      const saved = await updateAdminSettings(payload);
      setSettings(saved);
      toast.success('Configuración guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo guardar la configuración');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUploadWhatsappGreetingImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWaGreeting(true);
    try {
      const { image_url: imageUrl } = await uploadWhatsappTreeGreetingImage(file);
      const next = { ...settings, wa_tree_greeting_image_url: imageUrl };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo WhatsApp guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo subir la imagen');
    } finally {
      setUploadingWaGreeting(false);
      e.target.value = '';
    }
  };

  const handleClearWhatsappGreetingImage = async () => {
    setUploadingWaGreeting(true);
    try {
      const next = { ...settings, wa_tree_greeting_image_url: '' };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo quitada');
    } catch {
      toast.error('No se pudo quitar la imagen');
    } finally {
      setUploadingWaGreeting(false);
    }
  };

  const handleUploadWhatsappFollowupImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWaFollowup(true);
    try {
      const { image_url: imageUrl } = await uploadWhatsappTreeFollowupImage(file);
      const next = { ...settings, wa_tree_followup_image_url: imageUrl };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen adicional de bienvenida guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo subir la imagen');
    } finally {
      setUploadingWaFollowup(false);
      e.target.value = '';
    }
  };

  const handleClearWhatsappFollowupImage = async () => {
    setUploadingWaFollowup(true);
    try {
      const next = { ...settings, wa_tree_followup_image_url: '' };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen adicional quitada');
    } catch {
      toast.error('No se pudo quitar la imagen');
    } finally {
      setUploadingWaFollowup(false);
    }
  };

  const handleSendDailyReminders = async () => {
    setSendingDaily(true);
    try {
      const r = await sendDailyRemindersNow({ force: forceDailyResend });
      toast.success(
        `Recordatorios matutinos: ${r.sent} enviados` +
          (r.failed ? `, ${r.failed} fallidos` : '') +
          (r.skipped_no_phone ? `, ${r.skipped_no_phone} sin teléfono` : '')
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el envío');
      }
    } finally {
      setSendingDaily(false);
    }
  };

  const handleResetAllAppointments = async () => {
    if (resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán todas las citas en MongoDB y, si el servidor tiene Google Calendar y el worker WhatsApp configurados, también los eventos en el calendario y la caché de reservas por WhatsApp. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetAppointmentsBusy(true);
    try {
      const r = await superAdminResetAllAppointments(resetAppointmentsPhrase);
      setResetAppointmentsPhrase('');
      const parts = [`Eliminadas ${r.appointments_deleted ?? 0} citas en la base de datos.`];
      if (r.google_events_deleted > 0) {
        parts.push(`${r.google_events_deleted} evento(s) en Google Calendar.`);
      }
      if (r.booking_phone_stats_deleted > 0) {
        parts.push(`Inasistencias por teléfono: ${r.booking_phone_stats_deleted} registro(s) borrado(s).`);
      }
      if (r.whatsapp_cache_cleared && r.whatsapp_cache_keys_cleared > 0) {
        parts.push(
          r.whatsapp_full_reset
            ? `Historial WhatsApp (Redis): ${r.whatsapp_cache_keys_cleared} clave(s).`
            : `Caché citas WhatsApp: ${r.whatsapp_cache_keys_cleared} clave(s).`
        );
      }
      toast.success(parts.join(' '));
      if (r.google_calendar_note) {
        toast.info(String(r.google_calendar_note), { duration: 12000 });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el reset');
      }
    } finally {
      setResetAppointmentsBusy(false);
    }
  };

  const loadNoShowList = async () => {
    setNoShowListLoading(true);
    setNoShowListError('');
    try {
      const rows = await getBookingPhoneStats({
        minCount: 1,
        atLimitOnly: noShowAtLimitOnly,
      });
      setNoShowList(Array.isArray(rows) ? rows : []);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const msg =
        status === 404
          ? 'El servidor aún no tiene la API del listado. Actualiza backend y frontend en el servidor.'
          : typeof detail === 'string'
            ? detail
            : 'No se pudo cargar el listado de inasistencias';
      setNoShowListError(msg);
      setNoShowList([]);
      toast.error(msg);
    } finally {
      setNoShowListLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadNoShowList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, noShowAtLimitOnly]);

  const noShowFlagLabel = (flag) => {
    if (flag === 'transfer_required') return 'En límite — transferencia';
    if (flag === 'no_show_warning') return 'Aviso previo';
    return 'Sin restricción';
  };

  const formatNoShowUpdatedAt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
      return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso.slice(0, 16);
    }
  };

  const handleUnblockNoShowPhone = async (phone10) => {
    setUnblockingPhone(phone10);
    try {
      const r = await resetPhoneNoShowCount(phone10);
      toast.success(r.message || 'Teléfono desbloqueado.');
      await loadNoShowList();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo desbloquear');
    } finally {
      setUnblockingPhone(null);
    }
  };

  const handleResetNoShowPhone = async () => {
    const digits = resetNoShowPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Escribe el teléfono con al menos 10 dígitos.');
      return;
    }
    const phone10 = digits.slice(-10);
    setResetNoShowBusy(true);
    try {
      const r = await resetPhoneNoShowCount(phone10);
      setResetNoShowPhone('');
      toast.success(r.message || 'Inasistencias reiniciadas para ese teléfono.');
      await loadNoShowList();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo reiniciar el contador');
    } finally {
      setResetNoShowBusy(false);
    }
  };

  const handleResetAllClinicalRecords = async () => {
    if (resetClinicalPhrase.trim() !== SUPER_RESET_CLINICAL_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán TODOS los expedientes, historias clínicas, notas de evolución y fotos de evidencias. Las citas de la agenda NO se borran, solo se desvinculan. Esta acción no se puede deshacer. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetClinicalBusy(true);
    try {
      const r = await superAdminResetAllClinicalRecords(resetClinicalPhrase);
      setResetClinicalPhrase('');
      toast.success(
        `Reset clínico: ${r.patient_records_deleted ?? 0} expedientes, ${r.clinical_encounters_deleted ?? 0} notas de evolución, ${r.attachment_files_deleted ?? 0} archivos.`
      );
      if (r.appointments_note) {
        toast.info(String(r.appointments_note), { duration: 12000 });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el reset clínico');
      }
    } finally {
      setResetClinicalBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Configura variables dinámicas del sistema</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-primary" />
              Plantilla de saludo WhatsApp (menú del árbol)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Primer mensaje que ve el paciente en WhatsApp al escribir <em>menu</em> o al iniciar el chat
              (PodoclinicAM). Use <strong>*asteriscos*</strong> para negritas. Vacío = texto por defecto del
              servidor. La <strong>imagen de logo</strong> va con el menú como pie de foto; justo después se puede
              enviar una <strong>imagen adicional</strong> (promoción, aviso, etc.). El worker del puente debe alcanzar
              la <strong>URL pública base</strong> (tarjeta de recordatorios abajo).
            </p>
            <div className="space-y-2">
              <Label htmlFor="wa_tree_greeting_text">Plantilla de bienvenida (texto)</Label>
              <Textarea
                id="wa_tree_greeting_text"
                rows={5}
                value={settings.wa_tree_greeting_text ?? ''}
                onChange={(e) => handleChange('wa_tree_greeting_text', e.target.value)}
                placeholder={'🌸 Hola, bienvenido(a) a PodoclinicAM 🌸\n\nSerá un gusto ayudarte 😊'}
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Imagen de logo (con el menú como pie de foto)</Label>
              {settings.wa_tree_greeting_image_url ? (
                <div className="rounded-lg border border-border overflow-hidden max-w-xs bg-muted/30">
                  <img
                    src={resolveMediaUrl(settings.wa_tree_greeting_image_url)}
                    alt="Vista previa logo saludo WhatsApp"
                    className="w-full h-auto object-contain max-h-40"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin imagen de logo.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="max-w-sm cursor-pointer"
                  disabled={uploadingWaGreeting}
                  onChange={handleUploadWhatsappGreetingImage}
                />
                {settings.wa_tree_greeting_image_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingWaGreeting}
                    onClick={handleClearWhatsappGreetingImage}
                  >
                    Quitar logo
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="space-y-2 border-t border-border pt-4">
              <Label>Imagen adicional tras la bienvenida</Label>
              <p className="text-sm text-muted-foreground">
                Se envía en un segundo mensaje justo después del saludo con menú (solo imagen, sin texto).
              </p>
              {settings.wa_tree_followup_image_url ? (
                <div className="rounded-lg border border-border overflow-hidden max-w-xs bg-muted/30">
                  <img
                    src={resolveMediaUrl(settings.wa_tree_followup_image_url)}
                    alt="Vista previa imagen adicional WhatsApp"
                    className="w-full h-auto object-contain max-h-48"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin imagen adicional.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="max-w-sm cursor-pointer"
                  disabled={uploadingWaFollowup}
                  onChange={handleUploadWhatsappFollowupImage}
                />
                {settings.wa_tree_followup_image_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingWaFollowup}
                    onClick={handleClearWhatsappFollowupImage}
                  >
                    Quitar imagen adicional
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Recepción con IA (WhatsApp)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Activa una recepción conversacional con ChatGPT para agendar citas por WhatsApp de forma
              natural. Cuando está <strong>apagada</strong>, el bot funciona con el menú determinista de siempre
              (plantilla de saludo de arriba). La IA solo se usa si está encendida <strong>y</strong> hay un API
              key de OpenAI configurado. Apagar el interruptor <strong>no borra</strong> el API key: se conserva
              para cuando vuelvas a encenderla.
            </p>

            <FlowStep
              step="Encendido"
              title="Activar recepción con IA"
              hint="Al activarla, ChatGPT conduce la conversación de citas. Si la IA falla, el bot vuelve automáticamente al menú clásico."
            >
              <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                <Checkbox
                  checked={Boolean(settings.wa_ai_enabled)}
                  onCheckedChange={(checked) => handleChange('wa_ai_enabled', Boolean(checked))}
                />
                <span className="text-sm">Encender la recepción con IA en WhatsApp</span>
              </label>
            </FlowStep>

            <FlowStep
              step="Mismo día"
              title="Citas normales el día de hoy"
              hint="Aplica solo a citas podológicas normales por WhatsApp (IA y menú clásico). Las urgentes ya pueden agendarse hoy. Apagado = solo desde mañana."
            >
              <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                <Checkbox
                  checked={Boolean(settings.wa_allow_same_day_standard)}
                  onCheckedChange={(checked) =>
                    handleChange('wa_allow_same_day_standard', Boolean(checked))
                  }
                />
                <span className="text-sm">
                  Permitir agendar citas podológicas normales el mismo día (WhatsApp)
                </span>
              </label>
            </FlowStep>

            <FlowStep
              step="Credenciales"
              title="OpenAI / ChatGPT y API key"
              hint="Por defecto usa la API de OpenAI (ChatGPT). Puedes cambiar modelo o URL base si usas un endpoint compatible."
            >
              <div className="space-y-2">
                <Label htmlFor="wa_ai_api_key">API key</Label>
                <Input
                  id="wa_ai_api_key"
                  type="password"
                  autoComplete="new-password"
                  value={settings.wa_ai_api_key ?? ''}
                  onChange={(e) => handleChange('wa_ai_api_key', e.target.value)}
                  className="h-11 font-mono text-sm"
                  placeholder={
                    settings.wa_ai_api_key === WA_AI_API_KEY_MASK
                      ? 'API key guardada (déjala así para no cambiarla)'
                      : 'Pega aquí tu API key de OpenAI'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Por seguridad, la key no se muestra completa. Para conservarla, no edites este campo; para
                  cambiarla, escribe la nueva.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="wa_ai_model">Modelo</Label>
                  <Input
                    id="wa_ai_model"
                    value={settings.wa_ai_model ?? ''}
                    onChange={(e) => handleChange('wa_ai_model', e.target.value)}
                    className="h-11"
                    placeholder="gpt-4o-mini"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wa_ai_base_url">URL base (compatible OpenAI)</Label>
                  <Input
                    id="wa_ai_base_url"
                    value={settings.wa_ai_base_url ?? ''}
                    onChange={(e) => handleChange('wa_ai_base_url', e.target.value)}
                    className="h-11 font-mono text-sm"
                    placeholder="https://api.openai.com/v1"
                  />
                </div>
              </div>
            </FlowStep>

            <FlowStep
              step="Alcance"
              title="Rol y límites de la IA"
              hint="Describe el rol del negocio y qué NO debe responder. Esto evita que gaste respondiendo temas ajenos a la clínica."
            >
              <div className="space-y-2">
                <Label htmlFor="wa_ai_business_context">Contexto del negocio / instrucciones</Label>
                <Textarea
                  id="wa_ai_business_context"
                  rows={7}
                  value={settings.wa_ai_business_context ?? ''}
                  onChange={(e) => handleChange('wa_ai_business_context', e.target.value)}
                  className="mt-1 font-mono text-sm"
                  placeholder={
                    'Eres la recepción virtual de una clínica de podología. Ayuda solo a agendar, ' +
                    'consultar, reagendar o cancelar citas e informar sobre servicios y horarios. Si preguntan algo ' +
                    'ajeno a la clínica, declina amablemente y reconduce al agendado.'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Vacío = se usa el texto por defecto del servidor (clínica de podología).
                </p>
              </div>
            </FlowStep>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              Inasistencias y transferencia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Cuando marque «No llegó paciente» en Citas, se cuenta por teléfono. Las citas pendientes de ese número se
              muestran con borde ámbar. Al alcanzar el límite, el paciente{' '}
              <strong>sigue pudiendo agendar</strong> por WhatsApp; en el saludo y al confirmar verá el costo de
              transferencia ({'{amount}'} = monto configurado). Ya no se bloquea el número automáticamente.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="no_show_max_before_block">
                  Inasistencias antes de mostrar costo de transferencia al agendar
                </Label>
                <Input
                  id="no_show_max_before_block"
                  type="number"
                  min={1}
                  max={50}
                  className="mt-1 h-11 max-w-[160px]"
                  value={settings.no_show_max_before_block ?? 2}
                  onChange={(e) => handleChange('no_show_max_before_block', e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ej.: 2 = tras dos «No llegó paciente» se muestra la plantilla «al límite» con el monto.
                </p>
              </div>
              <div>
                <Label htmlFor="reset-no-show-phone">Reiniciar contador de inasistencias</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <Input
                    id="reset-no-show-phone"
                    className="h-11 max-w-xs"
                    placeholder="10 dígitos, ej. 8115556484"
                    value={resetNoShowPhone}
                    onChange={(e) => setResetNoShowPhone(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-11 rounded-full"
                    disabled={resetNoShowBusy}
                    onClick={handleResetNoShowPhone}
                  >
                    {resetNoShowBusy ? 'Reiniciando…' : 'Reiniciar contador'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Pone a cero las inasistencias del teléfono; deja de mostrarse el aviso de transferencia hasta que
                  vuelva a marcarse «No llegó paciente».
                </p>
              </div>
            </div>

            <div
              id="listado-inasistencias"
              className="rounded-xl border-2 border-primary/25 bg-primary/5 p-4 space-y-3 scroll-mt-24"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-foreground">Listado de teléfonos con inasistencias</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Números con «No llegó paciente» en Citas. Desbloquear quita el aviso de transferencia en WhatsApp.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    id="no-show-at-limit-only"
                    checked={noShowAtLimitOnly}
                    onCheckedChange={(v) => setNoShowAtLimitOnly(v === true)}
                  />
                  <Label htmlFor="no-show-at-limit-only" className="text-sm font-normal cursor-pointer">
                    Solo en límite de transferencia
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    disabled={noShowListLoading}
                    onClick={loadNoShowList}
                  >
                    {noShowListLoading ? 'Cargando…' : 'Actualizar'}
                  </Button>
                </div>
              </div>
              {noShowListError ? (
                <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                  {noShowListError}
                </p>
              ) : null}
              {noShowListLoading && noShowList.length === 0 && !noShowListError ? (
                <p className="text-sm text-muted-foreground">Cargando listado…</p>
              ) : noShowList.length === 0 && !noShowListError ? (
                <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border px-4 py-6 text-center bg-background">
                  No hay teléfonos con inasistencias registradas
                  {noShowAtLimitOnly ? ' en el límite configurado' : ''}. Marca «No llegó paciente» en una cita para
                  que aparezcan aquí.
                </p>
              ) : noShowList.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-border bg-background">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left">
                        <th className="px-3 py-2 font-medium">Teléfono</th>
                        <th className="px-3 py-2 font-medium">Inasistencias</th>
                        <th className="px-3 py-2 font-medium">Estado</th>
                        <th className="px-3 py-2 font-medium">Actualizado</th>
                        <th className="px-3 py-2 font-medium text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {noShowList.map((row) => (
                        <tr key={row.phone_digits_10} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 font-mono">{formatBlacklistPhone(row.phone_digits_10)}</td>
                          <td className="px-3 py-2">{row.no_show_count}</td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                row.booking_flag === 'transfer_required'
                                  ? 'text-amber-700 dark:text-amber-400 font-medium'
                                  : ''
                              }
                            >
                              {noShowFlagLabel(row.booking_flag)}
                            </span>
                            {row.in_wa_blacklist ? (
                              <span className="block text-xs text-destructive mt-0.5">
                                También en lista negra (más abajo en esta página)
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                            {formatNoShowUpdatedAt(row.updated_at)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="rounded-full"
                              disabled={unblockingPhone === row.phone_digits_10}
                              onClick={() => handleUnblockNoShowPhone(row.phone_digits_10)}
                            >
                              {unblockingPhone === row.phone_digits_10 ? '…' : 'Desbloquear'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="urgent_appointment_amount_label">Costo cita urgente (informativo WhatsApp)</Label>
                <Input
                  id="urgent_appointment_amount_label"
                  className="mt-1 h-11"
                  value={settings.urgent_appointment_amount_label ?? ''}
                  onChange={(e) => handleChange('urgent_appointment_amount_label', e.target.value)}
                  placeholder="$500 MXN"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Se muestra al iniciar y en la confirmación del flujo 5️⃣; no bloquea el agendado.
                </p>
              </div>
              <div>
                <Label htmlFor="booking_transfer_amount_label">Monto de transferencia (texto libre)</Label>
                <Input
                  id="booking_transfer_amount_label"
                  className="mt-1 h-11"
                  value={settings.booking_transfer_amount_label ?? ''}
                  onChange={(e) => handleChange('booking_transfer_amount_label', e.target.value)}
                  placeholder="$300 MXN"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="booking_transfer_at_limit_template">
                Texto al alcanzar el límite de inasistencias (use {'{amount}'})
              </Label>
              <Textarea
                id="booking_transfer_at_limit_template"
                rows={2}
                className="mt-1 font-mono text-sm"
                value={settings.booking_transfer_at_limit_template ?? ''}
                onChange={(e) => handleChange('booking_transfer_at_limit_template', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="booking_transfer_reminder_template">
                Recordatorio con inasistencias antes del límite (use {'{amount}'})
              </Label>
              <Textarea
                id="booking_transfer_reminder_template"
                rows={3}
                className="mt-1 font-mono text-sm"
                value={settings.booking_transfer_reminder_template ?? ''}
                onChange={(e) => handleChange('booking_transfer_reminder_template', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Historia clínica — consentimiento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Estos datos se insertan automáticamente en el paso 8 (consentimiento informado) y en el PDF al
              finalizar la historia.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="consent_podiatrist_name">Nombre del podólogo(a)</Label>
                <Input
                  id="consent_podiatrist_name"
                  className="mt-1 h-11"
                  value={settings.consent_podiatrist_name ?? ''}
                  onChange={(e) => handleChange('consent_podiatrist_name', e.target.value)}
                  placeholder="Ej. Dra. María García"
                />
              </div>
              <div>
                <Label htmlFor="consent_podiatrist_license">
                  Responsable de atención (opcional)
                </Label>
                <Input
                  id="consent_podiatrist_license"
                  className="mt-1 h-11"
                  value={settings.consent_podiatrist_license ?? ''}
                  onChange={(e) => handleChange('consent_podiatrist_license', e.target.value)}
                  placeholder="Ej. podólogo tratante o registro interno (no usar cédula falsa)"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {user && isSuperAdmin(user.role) ? (
          <Card className="border border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-900">
                <ClipboardList className="w-5 h-5 shrink-0" />
                Expedientes clínicos — reset total
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Solo <strong>super administrador</strong>. Borra todos los pacientes clínicos, expedientes, historias
                (wizard adulto), notas de evolución, fotos de evidencias y reinicia los folios HC- y NE- (
                <code className="text-xs">HC-año-00001</code> y <code className="text-xs">NE-año-00001</code>). No elimina usuarios del panel, productos, reseñas ni la
                configuración de consentimiento. Las <strong>citas de agenda</strong> permanecen; solo se quita el
                vínculo con expediente.
              </p>
              <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                  <Trash2 className="w-4 h-4 shrink-0" />
                  Borrar todas las historias clínicas
                </p>
                <div>
                  <Label htmlFor="reset-clinical-phrase" className="text-red-900">
                    Escribe la frase exacta para habilitar el botón
                  </Label>
                  <Input
                    id="reset-clinical-phrase"
                    value={resetClinicalPhrase}
                    onChange={(e) => setResetClinicalPhrase(e.target.value)}
                    className="mt-1 font-mono text-sm bg-white border-red-200"
                    placeholder={SUPER_RESET_CLINICAL_CONFIRM_PHRASE}
                    autoComplete="off"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-full gap-2"
                  disabled={
                    resetClinicalBusy ||
                    resetClinicalPhrase.trim() !== SUPER_RESET_CLINICAL_CONFIRM_PHRASE
                  }
                  onClick={handleResetAllClinicalRecords}
                >
                  <Trash2 className="w-4 h-4" />
                  {resetClinicalBusy ? 'Eliminando…' : 'Eliminar todas las historias clínicas'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              Lista negra WhatsApp
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Números a los que el bot <strong>no responderá</strong> (mensajes entrantes ignorados). Útil para
              spam o contactos que no quieres atender por el árbol automático. Se guardan los últimos 10 dígitos
              (México). Al pulsar <strong>Añadir</strong> o quitar un número, se guarda al instante en el servidor.
            </p>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[160px] space-y-2">
                <Label htmlFor="wa-blacklist-name">Nombre o nota (opcional)</Label>
                <Input
                  id="wa-blacklist-name"
                  type="text"
                  placeholder="Ej. Spam, proveedor X"
                  value={blacklistNameInput}
                  onChange={(e) => setBlacklistNameInput(e.target.value)}
                  maxLength={80}
                  className="h-11 mt-1"
                />
              </div>
              <div className="flex-1 min-w-[200px] space-y-2">
                <Label htmlFor="wa-blacklist-input">Teléfono a bloquear</Label>
                <Input
                  id="wa-blacklist-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="771 234 5678 o 527712345678"
                  value={blacklistInput}
                  onChange={(e) => setBlacklistInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBlacklistPhone();
                    }
                  }}
                  className="h-11 mt-1"
                />
              </div>
              <Button type="button" variant="outline" className="gap-2" onClick={handleAddBlacklistPhone}>
                <Plus className="w-4 h-4" />
                Añadir
              </Button>
            </div>
            {coerceBlacklistEntries(settings.wa_phone_blacklist).length > 0 ? (
              <ul className="space-y-2">
                {coerceBlacklistEntries(settings.wa_phone_blacklist).map((entry) => (
                  <li
                    key={entry.phone}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 bg-muted/30"
                  >
                    <div className="min-w-0">
                      {entry.name ? (
                        <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                      ) : null}
                      <p className={`font-mono text-sm ${entry.name ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {formatBlacklistPhone(entry.phone)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive shrink-0"
                      onClick={() => handleRemoveBlacklistPhone(entry.phone)}
                      aria-label={`Quitar ${entry.name || entry.phone}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No hay números bloqueados.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Recordatorios de citas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
              <Checkbox
                checked={settings.appointment_reminder_enabled}
                onCheckedChange={(checked) => handleChange('appointment_reminder_enabled', Boolean(checked))}
              />
              <span>Activar recordatorios automáticos por WhatsApp</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reminder-minutes">Tiempo de anticipación (minutos)</Label>
                <Input
                  id="reminder-minutes"
                  type="number"
                  min="1"
                  max="10080"
                  value={settings.appointment_reminder_minutes_before}
                  onChange={(e) => handleChange('appointment_reminder_minutes_before', e.target.value)}
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label htmlFor="reminder-interval">Frecuencia de revisión (segundos)</Label>
                <Input
                  id="reminder-interval"
                  type="number"
                  min="15"
                  max="3600"
                  value={settings.appointment_reminder_check_interval_sec}
                  onChange={(e) => handleChange('appointment_reminder_check_interval_sec', e.target.value)}
                  className="h-11 mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="daily-reminder-time">Hora del recordatorio del día (citas de hoy)</Label>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Input
                  id="daily-reminder-time"
                  type="time"
                  value={settings.appointment_daily_reminder_time || ''}
                  onChange={(e) => handleChange('appointment_daily_reminder_time', e.target.value)}
                  className="h-11 max-w-[220px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 rounded-full"
                  onClick={() => handleChange('appointment_daily_reminder_time', '')}
                >
                  Quitar hora
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                A esa hora (zona del calendario, p. ej. America/Mexico_City) se envía <strong>un solo lote</strong>{' '}
                por día (ventana de unos 20 minutos, p. ej. 08:00–08:20). No se envía al confirmar citas en el panel.
                Deja vacío para desactivar. Independiente del recordatorio por anticipación.
              </p>
            </div>

            <div>
              <Label htmlFor="daily-reminder-template">Plantilla recordatorio matutino (citas de hoy)</Label>
              <Textarea
                id="daily-reminder-template"
                rows={6}
                value={settings.appointment_daily_reminder_template}
                onChange={(e) => handleChange('appointment_daily_reminder_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Solo WhatsApp: pide al paciente responder *1* o *SÍ* para confirmar asistencia. Placeholders:{' '}
                {'{patient_name}'}, {'{date}'}, {'{time}'} (los enlaces web ya no son necesarios).
              </p>
            </div>

            <div>
              <Label htmlFor="daily-attendance-deadline-time">
                Hora límite de confirmación de asistencia (mismo día)
              </Label>
              <Input
                id="daily-attendance-deadline-time"
                type="time"
                value={settings.appointment_daily_attendance_deadline_time || '10:00'}
                onChange={(e) =>
                  handleChange('appointment_daily_attendance_deadline_time', e.target.value)
                }
                className="h-11 max-w-[220px] mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tras el recordatorio matutino (p. ej. 08:00), si el paciente no confirma antes de esta hora
                (p. ej. 10:00), la cita se cancela automáticamente y se suma al contador de cancelaciones de su
                teléfono.
              </p>
            </div>

            <div>
              <Label htmlFor="daily-auto-cancel-template">
                Plantilla WhatsApp al cancelar por no confirmar asistencia
              </Label>
              <Textarea
                id="daily-auto-cancel-template"
                rows={5}
                value={settings.appointment_daily_auto_cancel_template ?? ''}
                onChange={(e) =>
                  handleChange('appointment_daily_auto_cancel_template', e.target.value)
                }
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}.
              </p>
            </div>

            {user && isSuperAdmin(user.role) ? (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <p className="text-sm font-medium">Super administrador</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={forceDailyResend}
                    onCheckedChange={(c) => setForceDailyResend(Boolean(c))}
                  />
                  <span>Reenviar a todos los de hoy aunque ya se haya enviado el matutino</span>
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="rounded-full gap-2"
                  disabled={sendingDaily}
                  onClick={handleSendDailyReminders}
                >
                  <Sun className="w-4 h-4" />
                  {sendingDaily ? 'Enviando…' : 'Enviar recordatorios matutinos ahora'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Usa la plantilla matutina y la fecha de hoy en la zona del calendario. No afecta el recordatorio
                  por anticipación (minutos antes).
                </p>

                <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3">
                  <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 shrink-0" />
                    Reset masivo de citas
                  </p>
                  <p className="text-xs text-red-900/90 leading-relaxed">
                    Borra <strong>todas</strong> las citas en MongoDB (pendientes, confirmadas, canceladas e
                    inasistencias «No llegó paciente»), los contadores de inasistencia por teléfono, los eventos en{' '}
                    <strong>Google Calendar</strong> y el estado del bot en Redis (menú/árbol WhatsApp, pasos a medias,
                    dedup de citas). Los chats vuelven al menú inicial al escribir de nuevo. No elimina productos,
                    reseñas, usuarios ni la configuración del panel.
                  </p>
                  <div>
                    <Label htmlFor="reset-appointments-phrase" className="text-red-900">
                      Escribe la frase exacta para habilitar el botón
                    </Label>
                    <Input
                      id="reset-appointments-phrase"
                      value={resetAppointmentsPhrase}
                      onChange={(e) => setResetAppointmentsPhrase(e.target.value)}
                      className="mt-1 font-mono text-sm bg-white border-red-200"
                      placeholder={SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    className="rounded-full gap-2"
                    disabled={
                      resetAppointmentsBusy ||
                      resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE
                    }
                    onClick={handleResetAllAppointments}
                  >
                    <Trash2 className="w-4 h-4" />
                    {resetAppointmentsBusy ? 'Eliminando…' : 'Eliminar todas las citas'}
                  </Button>
                </div>
              </div>
            ) : null}

            <div>
              <Label htmlFor="public-base-url">URL pública base</Label>
              <Input
                id="public-base-url"
                value={settings.public_base_url}
                onChange={(e) => handleChange('public_base_url', e.target.value)}
                className="h-11 mt-1"
                placeholder="https://tudominio.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se usa para construir links de confirmación/cancelación/reagendado.
              </p>
            </div>

            <div>
              <Label htmlFor="reminder-template">Plantilla recordatorio por anticipación (antes de la cita)</Label>
              <Textarea
                id="reminder-template"
                rows={7}
                value={settings.appointment_reminder_template}
                onChange={(e) => handleChange('appointment_reminder_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se envía cuando falten los minutos configurados para la hora de la cita. Placeholders:{' '}
                {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{confirm_url}'}, {'{cancel_url}'},{' '}
                {'{reschedule_url}'}. Usa emojis (🌸 📅 🕒) como en el menú del bot.
                {'{manage_url}'} ya no se usa en recordatorios (se ignora si aparece en plantillas antiguas).
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              WhatsApp al confirmar la cita
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              El paciente solo recibe WhatsApp cuando pulsas <strong>Confirmar</strong> en Citas (las
              solicitudes desde el sitio quedan pendientes hasta entonces).
            </p>
            <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
              <Checkbox
                checked={settings.wa_confirmation_whatsapp_enabled}
                onCheckedChange={(checked) =>
                  handleChange('wa_confirmation_whatsapp_enabled', Boolean(checked))
                }
              />
              <span>Enviar WhatsApp de confirmación cuando el administrador confirma la cita</span>
            </label>

            <div>
              <Label htmlFor="confirmation-template">Plantilla de confirmación</Label>
              <Textarea
                id="confirmation-template"
                rows={6}
                value={settings.wa_confirmation_whatsapp_template}
                onChange={(e) => handleChange('wa_confirmation_whatsapp_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{reason}'}, {'{reason_line}'}.
                Usa emojis (🌸 📅 🕒 ✅) como en el menú del bot.
              </p>
            </div>

            <Button onClick={handleSave} className="w-full rounded-full gap-2" disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Settings'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
